import type { IconType } from "react-icons";
import {
    FaBullseye,
    FaUserPlus,
    FaRoute,
    FaFilter,
    FaHandshake,
    FaAddressBook,
    FaComments,
    FaRobot,
    FaBell,
    FaCogs,
    FaChartLine,
    FaChartPie,
    FaUsers,
    FaTachometerAlt,
    FaInbox,
} from "react-icons/fa";

export const crmBusinessImpact = [
    { value: 40, suffix: "%", label: "Faster Response Time", prefix: "" },
    { value: 3, suffix: "X", label: "Lead Visibility", prefix: "" },
    { value: 60, suffix: "%", label: "Less Manual Work", prefix: "" },
    { value: 98, suffix: "%", label: "Customer Satisfaction", prefix: "" },
];

export const crmCommandMetrics = [
    { label: "New Leads", value: "+25", trend: "Today", color: "#9e00ff" },
    { label: "Conversion Rate", value: "87%", trend: "This month", color: "#22c55e" },
    { label: "Follow-Ups Due", value: "12", trend: "Action needed", color: "#f59e0b" },
    { label: "Pipeline Value", value: "₹2.4M", trend: "Active deals", color: "#002454" },
];

export const crmPipelineStages = [
    { label: "New Leads", count: 42, value: "₹8.2L", color: "#9e00ff" },
    { label: "Contacted", count: 28, value: "₹6.1L", color: "#7a00cc" },
    { label: "Qualified", count: 17, value: "₹4.8L", color: "#7b00a8" },
    { label: "Proposal", count: 11, value: "₹3.2L", color: "#002454" },
    { label: "Negotiation", count: 6, value: "₹2.1L", color: "#003d7a" },
    { label: "Won", count: 4, value: "₹1.4L", color: "#22c55e" },
];

export const crmPipelineKpis = [
    { label: "Pipeline Value", value: "₹2.4M", change: "+12%" },
    { label: "Conversion Rate", value: "32%", change: "+5.2%" },
    { label: "Revenue Forecast", value: "₹18.6L", change: "Q2 target" },
    { label: "Monthly Performance", value: "+24%", change: "vs last month" },
];

export const crmIndustries: { name: string; icon: IconType; benefit: string; features: string[] }[] = [
    {
        name: "Real Estate",
        icon: FaAddressBook,
        benefit: "Track property inquiries, site visits, and deal closures in one pipeline.",
        features: ["Lead capture from portals", "Site visit scheduling", "Broker performance tracking"],
    },
    {
        name: "Education",
        icon: FaUsers,
        benefit: "Manage admissions, parent follow-ups, and enrollment pipelines effortlessly.",
        features: ["Admission lead tracking", "Parent communication", "Counselor dashboards"],
    },
    {
        name: "Healthcare",
        icon: FaTachometerAlt,
        benefit: "Coordinate patient inquiries, appointments, and care team workflows.",
        features: ["Patient inquiry management", "Appointment reminders", "Care team collaboration"],
    },
    {
        name: "Retail",
        icon: FaChartPie,
        benefit: "Unify customer data across channels and boost repeat purchases.",
        features: ["Omnichannel lead capture", "Customer loyalty tracking", "Sales team insights"],
    },
    {
        name: "Manufacturing",
        icon: FaCogs,
        benefit: "Manage B2B inquiries, quotations, and distributor relationships at scale.",
        features: ["RFQ management", "Distributor pipelines", "Order forecasting"],
    },
    {
        name: "Consulting",
        icon: FaHandshake,
        benefit: "Track proposals, client engagements, and project pipelines with clarity.",
        features: ["Proposal tracking", "Client engagement history", "Project pipeline views"],
    },
];

export const crmFaqItems = [
    {
        question: "What is HonestiIQ?",
        answer: "HonestiIQ is a complete customer growth platform that helps businesses capture leads, manage relationships, automate follow-ups, and close more deals — all from one unified CRM dashboard.",
    },
    {
        question: "Can I integrate WhatsApp?",
        answer: "Yes. HonestiIQ offers native WhatsApp CRM integration with a shared team inbox, automated responses, conversation tracking, and one-click lead creation from chats.",
    },
    {
        question: "Is HonestiIQ suitable for SMEs?",
        answer: "Absolutely. HonestiIQ is built for small and medium businesses that need enterprise-grade CRM capabilities without enterprise complexity or cost.",
    },
    {
        question: "Can multiple team members use it?",
        answer: "Yes. Multiple team members can collaborate with role-based access, shared inboxes, lead assignment rules, and real-time activity tracking.",
    },
    {
        question: "Does it support analytics and reporting?",
        answer: "HonestiIQ includes CRM Analytics with revenue trends, sales performance dashboards, lead source analysis, conversion metrics, and team productivity reports.",
    },
    {
        question: "Can I access it on mobile devices?",
        answer: "Yes. HonestiIQ is fully accessible on mobile devices, letting your team manage leads, customer profiles, follow-ups, and notifications from anywhere.",
    },
    {
        question: "Can workflows be customized?",
        answer: "You can customize lead assignment rules, follow-up sequences, pipeline stages, automated tasks, and notification triggers to match your sales process.",
    },
    {
        question: "How does HonestiIQ improve sales performance?",
        answer: "By centralizing leads, automating follow-ups, providing pipeline visibility, and delivering actionable CRM Analytics — so your team spends less time on admin and more time closing deals.",
    },
];

export const crmLeadFeatures: { icon: IconType; title: string; description: string }[] = [
    { icon: FaBullseye, title: "Lead Capture", description: "Collect leads from website forms, WhatsApp, email, and manual entry in one place." },
    { icon: FaUserPlus, title: "Lead Assignment", description: "Automatically route new leads to the right sales representative." },
    { icon: FaRoute, title: "Lead Tracking", description: "Monitor every interaction and touchpoint across the customer journey." },
    { icon: FaFilter, title: "Lead Qualification", description: "Score and prioritize leads based on engagement and fit." },
    { icon: FaHandshake, title: "Lead Conversion", description: "Turn qualified prospects into paying customers with guided workflows." },
];

export const crmContactFeatures = [
    { label: "Customer Records", description: "Centralized profiles with contact details, company info, and custom fields." },
    { label: "Communication History", description: "Full timeline of calls, emails, WhatsApp chats, and meetings." },
    { label: "Relationship Management", description: "Track account health, deal stages, and engagement over time." },
];


export const crmWhatsAppFeatures: { icon: IconType; title: string; description: string }[] = [
    { icon: FaInbox, title: "Shared Team Inbox", description: "Collaborate on WhatsApp conversations from one unified inbox." },
    { icon: FaRobot, title: "Automated Responses", description: "Reply instantly with smart templates and trigger-based messages." },
    { icon: FaComments, title: "Conversation Tracking", description: "Every chat is logged and linked to the customer record automatically." },
    { icon: FaUserPlus, title: "Lead Creation from Chats", description: "Convert WhatsApp inquiries into CRM leads with one click." },
    { icon: FaBell, title: "Follow-Up Automation", description: "Schedule reminders and automated follow-ups after every conversation." },
];

export const crmAnalyticsWidgets = [
    { icon: FaChartLine, title: "Revenue Trends", metric: "₹24.8L", change: "+18% this quarter" },
    { icon: FaChartPie, title: "Lead Sources", metric: "6 channels", change: "WhatsApp leads +32%" },
    { icon: FaUsers, title: "Team Productivity", metric: "94%", change: "quota attainment" },
    { icon: FaTachometerAlt, title: "Conversion Metrics", metric: "32.4%", change: "+5.2% vs last month" },
];
